// src/services/portalNotes.ts
// ----------------------------
// PURE helpers for the DFV Service PORTAL notes list (the portal's own internal notes,
// e.g. telemarketing — NOT synced to Unleashed). Stored as a JSON array in the existing
// `shelters.dfv_service_portal_notes` TEXT column (no migration): newest note first.
// Any legacy free-text value (from the old single-textarea) degrades gracefully to ONE
// undated note. No React / no DB — unit-tests cleanly.

export interface PortalNote {
  at: string | null; // ISO timestamp, null for a legacy note
  author: string | null; // who added it (email/name), null for a legacy note
  text: string;
}

// Parse the stored column into a notes array (already newest-first, as stored). A JSON
// array → its notes; anything else non-empty → a single legacy note holding the raw text.
export function parsePortalNotes(raw: string | null | undefined): PortalNote[] {
  const s = (raw ?? "").trim();
  if (!s) return [];
  if (s.startsWith("[")) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((n) => n && typeof n === "object" && typeof n.text === "string")
          .map((n) => ({
            at: typeof n.at === "string" ? n.at : null,
            author: typeof n.author === "string" ? n.author : null,
            text: String(n.text),
          }));
      }
    } catch {
      /* fall through to legacy */
    }
  }
  return [{ at: null, author: null, text: s }];
}

export function serializePortalNotes(notes: PortalNote[]): string {
  return JSON.stringify(notes);
}

// Prepend a new note (newest-first) and return the serialized column value. Returns the
// existing value unchanged when the note text is blank.
export function prependNote(
  raw: string | null | undefined,
  note: { text: string; author?: string | null; at?: string | null }
): string {
  const text = (note.text ?? "").trim();
  const existing = parsePortalNotes(raw);
  if (!text) return serializePortalNotes(existing);
  const entry: PortalNote = {
    at: note.at ?? new Date().toISOString(),
    author: note.author ?? null,
    text,
  };
  return serializePortalNotes([entry, ...existing]);
}
